<template>
  <div>
    <!-- Tabulation Sheets List Card -->
    <b-card class="tabulation-sheets-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>

      <!-- Table Header -->
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 tabulation-title">
            {{ isHpgsDomain ? "Qualitative Review" : "Tabulation Sheets" }}
          </h5>
          <small class="text-muted">
            {{
              isHpgsDomain
                ? "Manage and review qualitative reviews"
                : "Manage and review tabulation sheets"
            }}
          </small>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Class Filter -->
        <b-col xl="2" lg="2" md="4" sm="6" cols="12">
          <v-select
            v-model="filters.classID"
            :options="classes"
            :clearable="true"
            label="name"
            :reduce="(val) => val.id"
            placeholder="Select class"
            class="filter-control v-select"
            append-to-body
            @input="onClassFilterChange"
          />
        </b-col>

        <!-- Subject Filter -->
        <b-col xl="2" lg="2" md="4" sm="6" cols="12">
          <v-select
            v-model="filters.subjectID"
            :options="subjects"
            :clearable="true"
            label="subject"
            :reduce="(val) => val.id"
            placeholder="Select subject"
            :disabled="!filters.classID"
            class="filter-control"
            append-to-body
            @input="onFilterChange"
          />
        </b-col>

        <!-- Exam Type Filter -->
        <b-col xl="2" lg="2" md="4" sm="6" cols="12">
          <v-select
            v-model="filters.examTypeID"
            :options="examTypes"
            :clearable="true"
            label="examType"
            :reduce="(val) => val.id"
            placeholder="Select exam type"
            class="filter-control v-select"
            append-to-body
            @input="onFilterChange"
          />
        </b-col>

        <!-- Session Filter -->
        <b-col xl="2" lg="2" md="4" sm="6" cols="12">
          <v-select
            v-model="filters.sessionID"
            :options="sessions"
            :clearable="true"
            label="session"
            :reduce="(val) => val.id"
            placeholder="Select session"
            class="filter-control"
            append-to-body
            @input="onFilterChange"
          />
        </b-col>

        <!-- Type Filter -->
        <b-col
          v-if="!hidesTabulationTypePicker && !isMadinatulIlmDomain"
          xl="2"
          lg="2"
          md="4"
          sm="6"
          cols="12"
        >
          <v-select
            v-model="filters.type"
            :options="typeOptions"
            :clearable="true"
            label="text"
            :reduce="(val) => val.value"
            placeholder="Select type"
            class="filter-control v-select"
            append-to-body
            @input="onFilterChange"
          />
        </b-col>

        <!-- Action Buttons -->
        <b-col
          :xl="isHpgsDomain ? 4 : 2"
          :lg="isHpgsDomain ? 4 : 2"
          md="12"
          sm="12"
          cols="12"
          class="text-right action-buttons-col"
        >
          <div
            class="d-flex gap-2 align-items-center justify-content-end action-buttons-container"
          >
            <b-button
              v-if="rights.add && rights.edit && !isHpgsDomain"
              variant="outline-warning"
              @click="toggleSidebar(true)"
              class="action-btn-filter"
              :disabled="lockSubmitting"
            >
              <b-spinner v-if="lockSubmitting" small class="mr-1" />
              <feather-icon v-else icon="LockIcon" size="16" />
            </b-button>
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              @click="openAddPage"
              class="action-btn-filter add-sheet-btn"
            >
              <feather-icon icon="PlusIcon" size="16" class="mr-1" />
              {{ isHpgsDomain ? "Add Qualitative Review" : "Add Sheet" }}
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-wrapper">
        <b-table
          class="tabulation-sheets-table"
          :items="tabulationSheets"
          :fields="tableFields"
          :busy="dataLoading"
          :responsive="false"
          hover
          show-empty
          :empty-text="tableEmptyText"
        >
          <template #table-busy>
            <!--
            b-table renders #table-busy inside a single <td colspan="N">.
            Nest a full-width table here so skeleton cells align across all columns.
          -->
            <div class="skeleton-table-wrap">
              <table class="skeleton-table">
                <tbody>
                  <tr
                    v-for="n in 5"
                    :key="'skeleton-' + n"
                    class="skeleton-row"
                  >
                    <td
                      v-for="(field, index) in fields"
                      :key="'skeleton-cell-' + index"
                      class="skeleton-cell"
                    >
                      <div class="skeleton-loader"></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template #cell(sno)="{ index }">
            <span class="text-muted">{{
              (currentPage - 1) * perPage + index + 1
            }}</span>
          </template>

          <template #cell(className)="{ item }">
            <span class="table-cell-text">{{ item.className || "N/A" }}</span>
          </template>

          <template #cell(subjectName)="{ item }">
            <span class="table-cell-text">{{ item.subjectName || "N/A" }}</span>
          </template>

          <template #cell(examTypeName)="{ item }">
            <span class="table-cell-text">{{
              item.examTypeName || "N/A"
            }}</span>
          </template>

          <template #cell(sessionName)="{ item }">
            <span class="table-cell-text">{{ item.sessionName || "N/A" }}</span>
          </template>

          <template #cell(type)="{ item }">
            <b-badge variant="light-primary">{{
              getTypeDisplayLabel(item)
            }}</b-badge>
          </template>

          <template #cell(dueDate)="{ item }">
            <span class="table-cell-text">{{
              formatDate(item.dueDate) || "N/A"
            }}</span>
          </template>

          <template #cell(isLocked)="{ item }">
            <feather-icon
              :icon="item.isLocked ? 'LockIcon' : 'UnlockIcon'"
              :class="item.isLocked ? 'text-danger' : 'text-success'"
              size="18"
              :style="{
                opacity: item.isLocked ? 1 : 0.7,
              }"
            />
          </template>

          <template #cell(lockedStatus)="{ item }">
            <b-badge
              v-if="item.isLocked"
              variant="danger"
              style="
                background-color: #dc3545;
                color: white;
                padding: 0.25rem 0.5rem;
              "
            >
              Yes
            </b-badge>
            <b-badge v-else variant="success"> No </b-badge>
          </template>

          <template #cell(actions)="{ item }">
            <div
              class="d-flex align-items-center justify-content-end action-buttons-wrapper"
            >
              <b-button
                variant="gradient-primary"
                v-b-tooltip.hover.left
                title="View"
                size="sm"
                class="action-btn-table mr-50"
                :disabled="
                  viewLoading === item.id ||
                  editLoading === item.id ||
                  deleteLoading === item.id
                "
                @click="viewSheet(item)"
              >
                <b-spinner v-if="viewLoading === item.id" small type="grow" />
                <feather-icon v-else icon="EyeIcon" size="16" />
              </b-button>
              <b-button
                v-if="isHpgsDomain"
                variant="gradient-primary"
                v-b-tooltip.hover.left
                title="Print"
                size="sm"
                class="action-btn-table mr-50"
                :disabled="
                  viewLoading === item.id ||
                  editLoading === item.id ||
                  deleteLoading === item.id
                "
                @click="openPrintFormatPicker(item)"
              >
                <feather-icon icon="PrinterIcon" size="16" />
              </b-button>
              <b-button
                v-if="rights.edit"
                variant="outline-success"
                v-b-tooltip.hover.left
                title="Edit"
                size="sm"
                class="action-btn-outline edit-btn mr-50"
                :disabled="
                  viewLoading === item.id ||
                  editLoading === item.id ||
                  deleteLoading === item.id
                "
                @click="editSheet(item)"
              >
                <b-spinner v-if="editLoading === item.id" small type="grow" />
                <feather-icon v-else icon="EditIcon" size="16" />
              </b-button>
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.right
                title="Delete"
                size="sm"
                class="action-btn-outline delete-btn"
                :disabled="
                  viewLoading === item.id ||
                  editLoading === item.id ||
                  deleteLoading === item.id
                "
                @click="deleteSheet(item)"
              >
                <b-spinner v-if="deleteLoading === item.id" small type="grow" />
                <feather-icon v-else icon="Trash2Icon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Lock Sidebar -->
    <b-sidebar
      id="lock-sidebar"
      :visible="isSidebarOpen"
      bg-variant="white"
      backdrop
      shadow
      width="450px"
      no-header-close
      right
      @hidden="onSidebarHidden"
      @change="onSidebarChange"
      @shown="onSidebarShown"
      class="lock-sidebar"
    >
      <div class="sidebar-header-modern">
        <div class="sidebar-header-content">
          <div class="sidebar-title-wrapper">
            <h5 class="mb-0 sidebar-title">Set Due Date and Auto Lock</h5>
            <small class="text-muted"
              >Configure due date and auto-lock settings</small
            >
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="sidebar-close-btn"
            @click="toggleSidebar(false)"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
      </div>

      <div class="sidebar-body-modern">
        <b-form @submit.prevent="submitLock" class="sidebar-form-modern">
          <!-- Class Selection -->
          <b-form-group label="Class" class="modern-form-group">
            <v-select
              v-model="lockForm.classIDs"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="classes"
              label="name"
              :reduce="(val) => val.id"
              placeholder="Select classes"
              multiple
              :clearable="true"
              class="modern-select"
              append-to-body
              @input="onLockClassChange"
            />
            <small class="text-muted">Select one or more classes</small>
          </b-form-group>

          <!-- Section Selection -->
          <b-form-group label="Section" class="modern-form-group">
            <v-select
              :key="
                'lock-section-' +
                isSidebarOpen +
                '-' +
                lockSections.length +
                '-' +
                lockForm.classIDs.length
              "
              v-model="lockForm.sectionID"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="lockSections"
              label="section"
              :reduce="(val) => val.id"
              placeholder="Select section"
              :clearable="true"
              :disabled="!lockForm.classIDs || lockForm.classIDs.length === 0"
              class="modern-select"
              append-to-body
            />
            <small class="text-muted"
              >Leave empty or select 0 for all sections</small
            >
          </b-form-group>

          <!-- Exam Type Selection -->
          <b-form-group label="Exam Type" class="modern-form-group">
            <v-select
              v-model="lockForm.examTypeID"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="examTypes"
              label="examType"
              :reduce="(val) => val.id"
              placeholder="Select exam type"
              :clearable="true"
              class="modern-select"
              append-to-body
            />
          </b-form-group>

          <!-- Session Selection -->
          <b-form-group label="Session" class="modern-form-group">
            <v-select
              :key="'lock-session-' + isSidebarOpen + '-' + sessions.length"
              v-model="lockForm.sessionID"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="sessions"
              label="session"
              :reduce="(val) => val.id"
              placeholder="Select session"
              :clearable="true"
              class="modern-select"
            />
          </b-form-group>

          <!-- Type Selection -->
          <b-form-group
            v-if="!isMadinatulIlmDomain && !hidesTabulationTypePicker"
            label="Type"
            class="modern-form-group"
          >
            <v-select
              v-model="lockForm.type"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="typeOptions"
              label="text"
              :reduce="(val) => val.value"
              placeholder="Select type"
              :clearable="true"
              class="modern-select"
              append-to-body
            />
          </b-form-group>

          <!-- Due Date -->
          <b-form-group label="Due Date" class="modern-form-group">
            <flat-pickr
              v-model="lockForm.dueDate"
              :config="dueDateConfig"
              class="form-control modern-input"
              placeholder="Select due date"
              @on-change="onDueDateChange"
            />
            <small class="text-muted">Select date for auto-lock</small>
          </b-form-group>

          <!-- Action Buttons -->
          <div class="sidebar-footer-modern">
            <b-button
              variant="gradient-primary"
              type="submit"
              :disabled="lockSubmitting"
              block
              class="modern-btn-submit"
            >
              <b-spinner v-if="lockSubmitting" small type="grow" class="mr-1" />
              <template v-else>
                <feather-icon icon="LockIcon" size="16" class="mr-1" />
                <span>Set Lock</span>
              </template>
            </b-button>
          </div>
        </b-form>
      </div>
    </b-sidebar>

    <!-- Print Format Picker -->
    <b-modal
      v-if="isHpgsDomain"
      v-model="printFormatModal"
      size="lg"
      centered
      hide-footer
      hide-header
      modal-class="tabulation-format-modal"
      body-class="p-0"
      content-class="format-modal-content"
    >
      <div class="format-picker">
        <div class="format-picker__header">
          <div class="format-picker__header-text">
            <span class="format-picker__eyebrow">Academic Transcript</span>
            <h4 class="format-picker__title">Choose a print layout</h4>
            <p class="format-picker__intro">
              Printing for
              <strong>{{ printTargetLabel }}</strong>
              — each format uses the same sheet data with a different layout.
            </p>
          </div>
          <button
            type="button"
            class="format-picker__close"
            aria-label="Close"
            @click="printFormatModal = false"
          >
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <div class="format-picker__grid">
          <button
            v-for="fmt in printFormats"
            :key="'fmt-' + fmt.id"
            type="button"
            class="format-card"
            :class="'format-card--' + fmt.id"
            @click="confirmPrintFormat(fmt.id)"
          >
            <div class="format-card__top">
              <span class="format-card__num">{{ fmt.id }}</span>
              <span class="format-card__tag">{{ fmt.tag }}</span>
            </div>

            <div class="format-card__preview" :class="'preview-' + fmt.id">
              <!-- F1: centered classic -->
              <template v-if="fmt.id === 1">
                <span class="pv-logo" />
                <span class="pv-title" />
                <span class="pv-rule" />
                <span class="pv-abc"><i /><i /><i /></span>
                <span class="pv-rows"><i /><i /><i /></span>
              </template>
              <!-- F2: side mast + cards -->
              <template v-else-if="fmt.id === 2">
                <span class="pv-mast">
                  <i class="pv-logo-sm" /><i class="pv-mast-t" />
                </span>
                <span class="pv-idbar" />
                <span class="pv-card"><i /><i /></span>
                <span class="pv-card"><i /><i /></span>
              </template>
              <!-- F3: twin columns -->
              <template v-else-if="fmt.id === 3">
                <span class="pv-ribbon" />
                <span class="pv-twin">
                  <i /><i /><i /><i />
                </span>
              </template>
              <!-- F4: ledger + remarks -->
              <template v-else-if="fmt.id === 4">
                <span class="pv-band"><i /><b /></span>
                <span class="pv-grid4"><i /><i /><i /><i /></span>
                <span class="pv-ledger"><i /><i /><i /></span>
              </template>
              <!-- F5: framed certificate -->
              <template v-else>
                <span class="pv-frame">
                  <i class="pv-logo" />
                  <i class="pv-title" />
                  <i class="pv-rows"><em /><em /></i>
                </span>
              </template>
            </div>

            <div class="format-card__body">
              <strong>{{ fmt.title }}</strong>
              <small>{{ fmt.desc }}</small>
              <span class="format-card__best">{{ fmt.best }}</span>
            </div>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BBadge,
  BSpinner,
  BSidebar,
  BForm,
  BFormGroup,
  BModal,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";

export default {
  name: "TabulationSheetBuilder",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BBadge,
    BSpinner,
    BSidebar,
    BForm,
    BFormGroup,
    BModal,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      tabulationSheets: [],
      classes: [],
      classesData: [],
      sections: [],
      subjects: [],
      dataLoading: false,
      currentPage: 1,
      perPage: 30,
      totalCount: 0,
      rights: {},
      fields: [
        { key: "sno", label: "S.No", sortable: false },
        { key: "className", label: "Class" },
        { key: "subjectName", label: "Subject" },
        { key: "examTypeName", label: "Exam Type" },
        { key: "sessionName", label: "Session" },
        { key: "type", label: "Type" },
        { key: "dueDate", label: "Due Date" },
        { key: "isLocked", label: "Locked" },
        { key: "lockedStatus", label: "Yes/No" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-right",
          tdClass: "text-right",
        },
      ],
      filters: {
        classID: null,
        subjectID: null,
        examTypeID: null,
        sessionID: null,
        type: null,
      },
      examTypes: [],
      sessions: [],
      lockForm: {
        classIDs: [],
        sectionID: 0,
        examTypeID: null,
        sessionID: null,
        type: null,
        dueDate: "",
      },
      lockSections: [],
      lockSubmitting: false,
      isSidebarOpen: false,
      viewLoading: null,
      editLoading: null,
      deleteLoading: null,
      printFormatModal: false,
      printTargetItem: null,
      printFormats: [
        {
          id: 1,
          title: "Classic Formal",
          tag: "Elegant",
          desc:
            "Centered campus logo, serif title, double red rules, and a clean A · B · C marking table.",
          best: "",
        },
        {
          id: 2,
          title: "Modern Scorecard",
          tag: "Cards",
          desc:
            "Horizontal red header, student ID card block, and category cards with red tick boxes.",
          best: "",
        },
        {
          id: 3,
          title: "Twin Checklist",
          tag: "Compact",
          desc:
            "Red ribbon category labels with criteria in two columns — denser page use.",
          best: "",
        },
        {
          id: 4,
          title: "Official Ledger",
          tag: "Register",
          desc:
            "Red header band, 4-field student meta grid, A · B · C columns, and three signature lines.",
          best: "",
        },
        {
          id: 5,
          title: "Framed Certificate",
          tag: "2 Copies",
          desc:
            "Double red border frame, compact table layout — prints two blank copies.",
          best: "",
        },
      ],
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
    };
  },
  computed: {
    reportDomain() {
      return String(this.$store.state.report || "")
        .replace(/_/g, "")
        .toLowerCase()
        .trim();
    },
    isHpgsDomain() {
      return (
        this.reportDomain === "myskoolhpgs" ||
        this.reportDomain === "hpgs" ||
        this.reportDomain.includes("hpgs")
      );
    },
    isWhssDomain() {
      return (
        this.reportDomain === "myskoolwhss" ||
        this.reportDomain === "whss" ||
        this.reportDomain.includes("whss")
      );
    },
    hidesTabulationTypePicker() {
      return this.isHpgsDomain || this.isWhssDomain;
    },
    isMadinatulIlmDomain() {
      return (
        this.reportDomain === "myskoolmadinatulilm" ||
        this.reportDomain.includes("madinatulilm")
      );
    },
    typeOptions() {
      if (this.isMadinatulIlmDomain) {
        return [{ text: "Sections", value: "questions-base" }];
      }
      return [
        { text: "Questions Base", value: "questions-base" },
        { text: "Icon Base", value: "icon-base" },
        { text: "Class Test", value: "class-test" },
      ];
    },
    tableFields() {
      if (this.isHpgsDomain) {
        const hide = new Set(["type", "dueDate", "isLocked", "lockedStatus"]);
        return this.fields.filter((f) => !hide.has(f.key));
      }
      if (this.isWhssDomain) {
        const hide = new Set(["type"]);
        return this.fields.filter((f) => !hide.has(f.key));
      }
      if (this.isMadinatulIlmDomain) {
        const hide = new Set(["type"]);
        return this.fields.filter((f) => !hide.has(f.key));
      }
      return this.fields;
    },
    displayedRange() {
      const start =
        this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    tableEmptyText() {
      if (this.dataLoading) return "Loading...";
      return this.isHpgsDomain
        ? "No qualitative reviews found"
        : "No tabulation sheets found";
    },
    printTargetLabel() {
      const item = this.printTargetItem;
      if (!item) return "this sheet";
      const bits = [
        item.className,
        item.subjectName,
        item.examTypeName,
      ].filter(Boolean);
      return bits.length ? bits.join(" · ") : "this sheet";
    },
  },
  created() {
    // Initialize rights and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadClasses();
    this.loadExamTypes();
    this.loadSessions();
    this.loadTabulationSheets();
  },
  mounted() {
    // Simple setup - no complex handlers needed
  },
  watch: {
    "filters.classID"(newVal) {
      if (newVal) {
        this.loadSubjects();
      } else {
        this.subjects = [];
        this.filters.subjectID = null;
      }
    },
  },
  methods: {
    ...mapActions(["get", "post", "delete"]),
    getTypeDisplayLabel(item) {
      const type = (item && item.type) || "";
      if (
        this.isMadinatulIlmDomain &&
        (type === "questions-base" ||
          String(item.typeDisplayName || "")
            .toLowerCase()
            .includes("question"))
      ) {
        return "Sections";
      }
      return (item && (item.typeDisplayName || item.type)) || "N/A";
    },
    async loadClasses() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const classesData = response.data || response;
        this.classesData = Array.isArray(classesData) ? classesData : [];
        // Map classes similar to resource.vue
        const mappedClasses = this.classesData
          .map((el) => el.cls)
          .filter((cls) => cls && cls.id);
        this.$set(this, "classes", mappedClasses);
        console.log("Classes loaded:", this.classes.length, this.classes);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      }
    },
    loadSectionsFromClass(classId) {
      if (!classId) {
        this.sections = [];
        return;
      }
      const selectedClassData = this.classesData.find(
        (item) => item.cls.id === classId
      );
      if (selectedClassData && selectedClassData.sections) {
        this.sections = selectedClassData.sections.map((sec) => ({
          id: sec.id,
          section: sec.section,
          name: sec.section,
        }));
      } else {
        this.sections = [];
      }
    },
    async loadSubjects() {
      if (!this.filters.classID) {
        this.subjects = [];
        return;
      }
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        this.subjects = await this.get(obj);
      } catch (error) {
        console.error("Error loading subjects:", error);
        this.subjects = [];
      }
    },
    async loadExamTypes() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "ExamTypes/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&status=active&page=1&pageSize=1000",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const examTypesData = response.data || response;
        this.examTypes = Array.isArray(examTypesData) ? examTypesData : [];
        console.log(
          "Exam Types loaded:",
          this.examTypes.length,
          this.examTypes
        );
      } catch (error) {
        console.error("Error loading exam types:", error);
        this.examTypes = [];
      }
    },
    async loadSessions() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Sessions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const sessionsData = response.data || response;
        const sessionsArray = Array.isArray(sessionsData) ? sessionsData : [];
        this.$set(this, "sessions", sessionsArray);
        console.log("Sessions loaded:", this.sessions.length, this.sessions);
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },
    async loadTabulationSheets() {
      this.dataLoading = true;
      try {
        const userID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.userLinkID ||
          this.$store.state.userData.localID ||
          0;
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          userID: userID,
          page: this.currentPage,
          pageSize: this.perPage,
        });

        if (this.filters.classID) {
          params.append("classId", this.filters.classID);
        }
        if (this.filters.subjectID) {
          params.append("subjectId", this.filters.subjectID);
        }
        if (this.filters.examTypeID) {
          params.append("examTypeId", this.filters.examTypeID);
        }
        if (this.filters.sessionID) {
          params.append("sessionId", this.filters.sessionID);
        }
        if (this.isMadinatulIlmDomain) {
          // Frontend-only: list Sections sheets (API type stays questions-base)
          params.append("type", "questions-base");
        } else if (this.filters.type) {
          params.append("type", this.filters.type);
        }

        const obj = {
          url:
            this.$store.state.domain +
            "TabulationSheet/GetByPagination?" +
            params.toString(),
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);
        if (res && res.status === "success" && Array.isArray(res.data)) {
          this.tabulationSheets = res.data;
          this.totalCount = res.totalCount || 0;
        } else if (res && Array.isArray(res.data)) {
          this.tabulationSheets = res.data;
          this.totalCount = res.total || res.totalCount || 0;
        } else {
          this.tabulationSheets = [];
          this.totalCount = 0;
        }
      } catch (error) {
        console.error("Error loading tabulation sheets:", error);
        this.tabulationSheets = [];
        this.totalCount = 0;
      } finally {
        this.dataLoading = false;
      }
    },
    onFilterChange() {
      this.currentPage = 1;
      this.loadTabulationSheets();
    },
    onClassFilterChange() {
      if (this.filters.classID) {
        this.loadSubjects();
      } else {
        this.subjects = [];
        this.filters.subjectID = null;
      }
      this.onFilterChange();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadTabulationSheets();
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.loadTabulationSheets();
    },
    openPrintFormatPicker(item) {
      // Custom Academic Transcript formats are HPGS-only
      if (!this.isHpgsDomain) {
        this.printSheetLegacy(item);
        return;
      }
      this.printTargetItem = item;
      this.printFormatModal = true;
    },
    printSheetLegacy(item) {
      const domain =
        this.reportDomain ||
        String(this.$store.state.report || "").replace(/_/g, "");
      const url =
        `https://${domain}.myskool.app/Exams/Tabulation?dbb=` +
        this.$store.state.userData.db +
        "&cID=" +
        (item.campusID || this.$store.state.userData.cId) +
        "&cls=" +
        item.classId +
        "&sec=" +
        (item.sectionId || 0) +
        "&examID=" +
        item.examTypeId +
        "&sessionID=" +
        item.sessionId +
        "&subjectID=" +
        item.subjectId +
        "&type=" +
        (item.type || "") +
        "&sIDs=&date=";
      window.open(url, "_blank");
    },
    confirmPrintFormat(formatId) {
      if (!this.isHpgsDomain) return;
      const item = this.printTargetItem;
      if (!item) return;
      this.printFormatModal = false;
      const routeData = this.$router.resolve({
        name: "tabulation-sheet-print",
        query: {
          id: item.id,
          db: this.$store.state.userData.db,
          cID: item.campusID || this.$store.state.userData.cId,
          format: formatId,
          type: item.type || "",
          className: item.className || "",
          sectionName: item.sectionName || "",
          examTypeName: item.examTypeName || "",
          sessionName: item.sessionName || "",
          subjectName: item.subjectName || "",
          campusName: this.$store.state.branchName || "",
        },
      });
      window.open(routeData.href, "_blank");
      this.printTargetItem = null;
    },
    openAddPage() {
      this.$router.push({ name: "tabulation-sheet-add" });
    },
    async viewSheet(item) {
      this.viewLoading = item.id;
      try {
        await this.$router.push({
          name: "tabulation-sheet-view",
          params: { id: item.id },
        });
      } catch (error) {
        console.error("Error navigating to view:", error);
      } finally {
        this.viewLoading = null;
      }
    },
    async editSheet(item) {
      this.editLoading = item.id;
      try {
        await this.$router.push({
          name: "tabulation-sheet-edit",
          params: { id: item.id },
        });
      } catch (error) {
        console.error("Error navigating to edit:", error);
      } finally {
        this.editLoading = null;
      }
    },
    async deleteSheet(item) {
      const result = await this.$swal.fire({
        title: "Are you sure?",
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        this.deleteLoading = item.id;
        try {
          const userID =
            this.$store.state.userData.userID ||
            this.$store.state.userData.userLinkID ||
            this.$store.state.userData.localID ||
            0;
          const obj = {
            url:
              this.$store.state.domain +
              "TabulationSheet/Delete?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId +
              "&id=" +
              item.id +
              "&userID=" +
              userID,
            message: this.isHpgsDomain
              ? "Qualitative review deleted successfully."
              : "Tabulation sheet deleted successfully.",
            context: this,
            token: this.$store.state.userData.token,
          };
          const success = await this.delete(obj);
          if (success) {
            this.loadTabulationSheets();
          }
        } catch (error) {
          console.error("Error deleting sheet:", error);
        } finally {
          this.deleteLoading = null;
        }
      }
    },
    async toggleSidebar(open) {
      this.isSidebarOpen = open;
      if (open) {
        // Reset form
        this.lockForm = {
          classIDs: [],
          sectionID: 0,
          examTypeID: null,
          sessionID: null,
          type: null,
          dueDate: "",
        };
        this.lockSections = [];

        // Ensure all data is loaded - use await to ensure data is ready
        if (!this.classes || this.classes.length === 0) {
          await this.loadClasses();
        }
        if (!this.examTypes || this.examTypes.length === 0) {
          await this.loadExamTypes();
        }
        if (!this.sessions || this.sessions.length === 0) {
          await this.loadSessions();
        }

        // Force reactivity update
        await this.$nextTick();
        console.log(
          "Sidebar opened - Classes:",
          this.classes.length,
          "ExamTypes:",
          this.examTypes.length,
          "Sessions:",
          this.sessions.length
        );
        console.log("Classes data:", this.classes);
        console.log("ExamTypes data:", this.examTypes);
        console.log("Sessions data:", this.sessions);
      }
    },
    onSidebarHidden() {
      this.isSidebarOpen = false;
    },
    onSidebarChange(visible) {
      this.isSidebarOpen = visible;
    },
    onSidebarShown() {
      // Sidebar is now shown
    },
    onLockClassChange(selectedClassIDs) {
      // Reset section when classes change
      this.lockForm.sectionID = 0;
      this.lockSections = [];

      if (!selectedClassIDs || selectedClassIDs.length === 0) {
        return;
      }

      // Load sections from selected classes
      this.loadLockSections(selectedClassIDs);
    },
    loadLockSections(selectedClassIDs) {
      const allSections = [];
      const sectionMap = new Map();

      // Add "All Sections" option
      allSections.push({
        id: 0,
        section: "All Sections",
        name: "All Sections",
      });

      if (
        this.classesData &&
        Array.isArray(this.classesData) &&
        selectedClassIDs &&
        selectedClassIDs.length > 0
      ) {
        selectedClassIDs.forEach((classID) => {
          const classData = this.classesData.find(
            (item) => item.cls && item.cls.id === classID
          );
          if (
            classData &&
            classData.sections &&
            Array.isArray(classData.sections)
          ) {
            classData.sections.forEach((sec) => {
              if (sec && sec.id && !sectionMap.has(sec.id)) {
                sectionMap.set(sec.id, sec);
                allSections.push({
                  id: sec.id,
                  section: sec.section || sec.name || `Section ${sec.id}`,
                  name: sec.section || sec.name || `Section ${sec.id}`,
                });
              }
            });
          }
        });
      }

      this.lockSections = allSections;
    },
    onDueDateChange(selectedDates, dateStr) {
      this.lockForm.dueDate = dateStr;
    },
    formatDate(dateString) {
      if (!dateString) return null;
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        return dateString;
      }
    },
    async submitLock() {
      // Validation
      if (!this.lockForm.classIDs || this.lockForm.classIDs.length === 0) {
        this.$bvToast.toast("Please select at least one class", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      if (!this.lockForm.examTypeID) {
        this.$bvToast.toast("Please select exam type", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      if (!this.lockForm.sessionID) {
        this.$bvToast.toast("Please select session", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      if (
        !this.isMadinatulIlmDomain &&
        !this.hidesTabulationTypePicker &&
        !this.lockForm.type
      ) {
        this.$bvToast.toast("Please select type", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      if (!this.lockForm.dueDate) {
        this.$bvToast.toast("Please select due date", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.lockSubmitting = true;

      try {
        // Format date to ISO string with time set to end of day
        let dueDateISO;
        if (this.lockForm.dueDate) {
          // If date is in Y-m-d format, parse it correctly
          const dateStr = this.lockForm.dueDate;
          const date = dateStr.includes("T")
            ? new Date(dateStr)
            : new Date(dateStr + "T23:59:59");
          date.setHours(23, 59, 59, 999);
          dueDateISO = date.toISOString();
        } else {
          this.$bvToast.toast("Please select due date", {
            title: "Validation Error",
            variant: "warning",
            solid: true,
          });
          this.lockSubmitting = false;
          return;
        }

        const payload = {
          classIDs: this.lockForm.classIDs,
          sectionID:
            this.lockForm.sectionID !== null &&
            this.lockForm.sectionID !== undefined
              ? this.lockForm.sectionID
              : 0,
          examTypeID: this.lockForm.examTypeID,
          sessionID: this.lockForm.sessionID,
          type: this.isMadinatulIlmDomain
            ? "questions-base"
            : this.hidesTabulationTypePicker
            ? "icon-base"
            : this.lockForm.type,
          dueDate: dueDateISO,
        };

        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const cID = this.$store.state.userData.cId;
        const userID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.userLinkID ||
          this.$store.state.userData.localID ||
          0;

        const url = `${this.$store.state.domain}TabulationSheet/SetDueDateAndAutoLock?db=${dbName}&cID=${cID}&userID=${userID}`;

        const response = await this.post({
          url,
          body: payload,
          token: this.$store.state.userData.token,
          message: "Due date and auto-lock set successfully",
          context: this,
        });

        if (response) {
          this.toggleSidebar(false);
          this.loadTabulationSheets();
        }
      } catch (error) {
        console.error("Error setting lock:", error);
        this.$bvToast.toast("Failed to set due date and lock", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.lockSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Tabulation Sheets List Card - Matching commentbank.vue exactly
.tabulation-sheets-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  ::v-deep .card-body {
    padding: 0;
  }

  // Gradient Line Wrapper - Aligned with content, shorter length
  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  // Filter row styling inside card
  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
  }
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-row-single-line {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;
  align-items: center;
  background: transparent !important;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
  }

  > [class*="col-"] {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    display: flex;
    align-items: center;
    background: transparent !important;
  }

  // Ensure buttons column is right-aligned
  > [class*="col-"].text-right {
    justify-content: flex-end;
  }

  // Action buttons column padding
  > [class*="col-"].action-buttons-col {
    padding-right: 1.5rem !important;
  }

  // Action buttons container
  .action-buttons-container {
    padding-right: 0.5rem;
  }

  // Add Sheet button spacing
  .add-sheet-btn {
    margin-right: 0 !important;
    white-space: nowrap;
    flex-shrink: 0;
    line-height: 1.2;
  }

  // Mobile responsive adjustments
  @media (max-width: 768px) {
    > [class*="col-"] {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .action-btn-filter {
      width: 100%;
      justify-content: center;
    }
  }
}

// Filter Control - Consistent height and styling for all filters
.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

// v-select filter control styling - Remove double layer
.filter-control.v-select {
  width: 100%;
  height: auto !important;
  min-height: auto !important;
  border: none !important;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;

  // Remove any wrapper backgrounds
  &::before,
  &::after {
    display: none !important;
  }

  ::v-deep .v-select {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;

    &::before,
    &::after {
      display: none !important;
    }
  }

  ::v-deep .vs__dropdown-toggle {
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 10px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.5rem 0.75rem !important;
    background: #fff !important;
    transition: all 0.2s ease;
    margin: 0 !important;
    box-shadow: none !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
    }
  }

  // Remove any wrapper backgrounds
  ::v-deep .vs__dropdown-toggle > * {
    background: transparent !important;
  }

  ::v-deep .vs__selected-options {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__search-container {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__selected {
    background: transparent !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
  }

  ::v-deep .vs__search {
    background: transparent !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 10000 !important;
    position: fixed !important;
    background: #ffffff !important;
    border: 2px solid #e0d8ff !important;
    box-shadow: 0 8px 24px rgba(115, 103, 240, 0.15) !important;
    border-radius: 12px !important;
    margin-top: 4px !important;
    max-height: 300px !important;
    overflow-y: auto !important;
    min-width: 200px !important;

    .vs__dropdown-option {
      background-color: #ffffff !important;
      background-image: none !important;
      padding: 0.75rem 1rem !important;
      color: #2d1b69 !important;
      font-weight: 500 !important;

      &:hover {
        background-color: #f9fafb !important;
        background-image: none !important;
        color: #1f2937 !important;
      }

      &--highlight {
        background-color: #f3f4f6 !important;
        background-image: none !important;
        color: #1f2937 !important;
        font-weight: 600 !important;
      }
    }
  }
}

// Action Button Filter
.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.gap-2 {
  gap: 0.5rem;
}

.table-header {
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

.tabulation-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

// Pagination Controls - Top (Compact Single Line)
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }

  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }

  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

// Page Size Wrapper
.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

// Compact Page Size Select
.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }

  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }

  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;

    &:hover {
      background-color: #f9fafb !important;
      color: #7c5dfa !important;
    }

    &--selected {
      background-color: #f3f0ff !important;
      color: #7c5dfa !important;
      font-weight: 600 !important;
    }
  }
}

// Pagination Count Display
.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }

  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

// Compact Pagination Wrapper
.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }

    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

.table-scroll-wrapper {
  margin-top: 1rem;
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  border-radius: 12px;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;

    &:hover {
      background: #94a3b8;
    }
  }

  @media (max-width: 768px) {
    margin-top: 0.5rem;
    // Force horizontal scroll on mobile
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;

    // Add shadow indicators for scroll
    position: relative;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: 30px;
      height: 100%;
      background: linear-gradient(
        to left,
        rgba(255, 255, 255, 0.9),
        transparent
      );
      pointer-events: none;
      z-index: 10;
    }
  }
}

// Tabulation Sheets Table - Modern Compact Design
.tabulation-sheets-table {
  margin-top: 0;
  border-radius: 12px;
  overflow: visible;
  border: 1px solid #e5e7eb;
  width: 100%;
  table-layout: auto;

  @media (max-width: 768px) {
    min-width: 800px; // Minimum width to show all columns properly
    width: 100%;
    table-layout: fixed;
  }

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: top;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;

      td {
        background: #f8fafc;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;

      td {
        background: #ffffff;
      }
    }

    &:hover {
      background: #f1f5f9 !important;
      border-left-color: #7c5dfa;
      box-shadow: -2px 0 8px rgba(124, 93, 250, 0.1);
      z-index: 1;
      position: relative;

      td {
        background: transparent !important;
        color: #1e293b;
        font-weight: 500;
      }
    }

    &:active {
      transform: none;
    }
  }

  // Mobile Responsive Styles - Keep table structure, make scrollable
  @media (max-width: 768px) {
    ::v-deep {
      // Keep table structure, just adjust sizing
      table {
        width: 100%;
        min-width: 800px;
        table-layout: fixed;
        border-collapse: separate;
        border-spacing: 0;
      }

      th {
        padding: 0.75rem 0.5rem !important;
        font-size: 0.7rem !important;
        white-space: nowrap;
        position: sticky;
        top: 0;
        z-index: 10;
        background: linear-gradient(
          180deg,
          #ffffff 0%,
          #f8fafc 100%
        ) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        overflow: hidden;
        text-overflow: ellipsis;
      }

      td {
        padding: 0.75rem 0.5rem !important;
        font-size: 0.8125rem !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: middle;
      }

      // Column width adjustments for mobile - Fixed widths
      th:nth-child(1),
      td:nth-child(1) {
        // S.No column
        width: 70px !important;
        min-width: 70px !important;
        max-width: 70px !important;
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
      }

      th:nth-child(2),
      td:nth-child(2) {
        // Class column
        width: auto !important;
        min-width: 150px !important;
        white-space: normal;
        word-break: break-word;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
      }

      th:nth-child(3),
      td:nth-child(3) {
        // Subject column
        width: auto !important;
        min-width: 150px !important;
        white-space: normal;
        word-break: break-word;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
      }

      th:nth-child(4),
      td:nth-child(4) {
        // Exam Type column
        width: auto !important;
        min-width: 150px !important;
        white-space: normal;
        word-break: break-word;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
      }

      th:nth-child(5),
      td:nth-child(5) {
        // Session column
        width: auto !important;
        min-width: 120px !important;
        white-space: normal;
        word-break: break-word;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
      }

      th:nth-child(6),
      td:nth-child(6) {
        // Type column
        width: 120px !important;
        min-width: 120px !important;
        max-width: 120px !important;
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
      }

      // Actions column (last column)
      th:last-child,
      td:last-child {
        width: 160px !important;
        min-width: 160px !important;
        max-width: 160px !important;
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
      }

      // Adjust action buttons container for mobile
      .d-flex {
        gap: 0.5rem !important;
        justify-content: center !important;
        flex-wrap: nowrap !important;
      }

      // Adjust action buttons for mobile
      .action-btn-table {
        width: 36px !important;
        height: 36px !important;
        padding: 0 !important;
        min-width: 36px !important;
        flex-shrink: 0;
      }

      .action-btn-outline {
        width: 36px !important;
        height: 36px !important;
        padding: 0 !important;
        min-width: 36px !important;
        flex-shrink: 0;
      }
    }
  }

  // Tablet styles (768px - 992px)
  @media (min-width: 769px) and (max-width: 992px) {
    ::v-deep {
      th,
      td {
        padding: 0.625rem 0.5rem;
        font-size: 0.8125rem;
      }

      .action-btn-table {
        width: 32px;
        height: 32px;
        padding: 0;
      }
    }
  }

  // Small mobile (max 576px)
  @media (max-width: 576px) {
    ::v-deep {
      td {
        padding: 0.625rem 0.5rem !important;
        font-size: 0.8125rem;
        min-height: 40px;
      }

      .action-btn-table {
        width: 36px;
        height: 36px;
        padding: 0;
        min-width: 36px;
      }
    }
  }
}

.table-cell-text {
  color: #475569;
  font-weight: 500;
  font-size: 0.875rem;
}

// Skeleton Loader Styles - Premium Animated Effect
// b-table wraps #table-busy in one colspan cell; force that cell full-width.
.tabulation-sheets-table {
  ::v-deep .b-table-busy-slot > td {
    padding: 0 !important;
    width: 100% !important;
  }
}

.skeleton-table-wrap {
  width: 100%;
  display: block;
}

.skeleton-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;

  &:nth-child(1) {
    animation-delay: 0s;
  }

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  &:nth-child(4) {
    animation-delay: 0.45s;
  }

  &:nth-child(5) {
    animation-delay: 0.6s;
  }

  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;
    transform: none !important;

    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;
  width: auto;

  &:nth-child(even) {
    background: #fafbfc !important;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.03) 50%,
      transparent 100%
    );
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
    pointer-events: none;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
    filter: blur(8px);
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.08) 30%,
      rgba(157, 122, 255, 0.12) 50%,
      rgba(124, 93, 250, 0.08) 70%,
      transparent 100%
    );
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes skeletonBorderGlow {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes skeletonColorWave {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

// Action Buttons
.action-buttons-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-right: 0;
}

// Ensure actions column header and cells are right-aligned
::v-deep .tabulation-sheets-table {
  thead th:last-child {
    text-align: right !important;
    padding-right: 1rem;
  }

  tbody td:last-child {
    text-align: right !important;
    padding-right: 1rem;
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

  &.edit-btn {
    border-color: #10b981 !important;
    color: #10b981 !important;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);

    &:hover:not(:disabled) {
      background: #f0fdf4 !important;
      border-color: #059669 !important;
      color: #059669 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
    }
  }

  &.delete-btn {
    &:hover:not(:disabled) {
      background: #fef2f2 !important;
      border-color: #dc2626 !important;
      color: #dc2626 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Simple Sidebar Styling
::v-deep #lock-sidebar .b-sidebar-header {
  display: none !important;
}

.lock-sidebar {
  ::v-deep .b-sidebar-body {
    padding: 0 !important;
  }
}

.sidebar-header-modern {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-bottom: 2px solid #f0edff;
  padding: 1.25rem 1.5rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .sidebar-header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
    z-index: 1;
  }

  .sidebar-title-wrapper {
    flex: 1;
  }

  .sidebar-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  .sidebar-close-btn {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid #e0d8ff;
    background: #fff;
    color: #64748b;
    transition: all 0.3s ease;

    &:hover {
      background: #f3f0ff;
      border-color: #b8a9ff;
      color: #7c5dfa;
    }
  }
}

.sidebar-body-modern {
  padding: 1rem;
}

.sidebar-form-modern {
  .modern-form-group {
    margin-bottom: 1rem;

    label {
      display: block;
      margin-bottom: 0.5rem;
    }
  }

  .modern-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .modern-select {
    width: 100%;
  }
}

.sidebar-footer-modern {
  padding: 1rem;
  border-top: 1px solid #ddd;
}

.format-picker {
  padding: 0;
  background: #fff;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 1.35rem 1.5rem 1rem;
    border-bottom: 2px solid #c41e1e;
    background: linear-gradient(180deg, #fffafa 0%, #ffffff 100%);
  }

  &__eyebrow {
    display: inline-block;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #c41e1e;
    margin-bottom: 4px;
  }

  &__title {
    margin: 0 0 6px;
    font-size: 1.2rem;
    font-weight: 800;
    color: #1a1a1a;
    letter-spacing: -0.02em;
  }

  &__intro {
    color: #64748b;
    font-size: 0.88rem;
    margin: 0;
    line-height: 1.45;
    max-width: 520px;

    strong {
      color: #c41e1e;
      font-weight: 700;
    }
  }

  &__close {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid #f0c8c8;
    background: #fff;
    color: #c41e1e;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: #fdf2f2;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 14px;
    padding: 1.25rem 1.5rem 1.6rem;
  }
}

.format-card {
  appearance: none;
  border: 1.5px solid #f0dede;
  background: #fff;
  border-radius: 16px;
  padding: 12px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #c41e1e;
    box-shadow: 0 10px 28px rgba(196, 30, 30, 0.14);
    transform: translateY(-3px);
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__num {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #c41e1e;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__tag {
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #c41e1e;
    background: #fdf2f2;
    border: 1px solid #f0c8c8;
    padding: 3px 8px;
    border-radius: 999px;
  }

  &__preview {
    height: 92px;
    border-radius: 10px;
    background: #fff;
    border: 1px solid #f0dede;
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 5px;
    overflow: hidden;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 4px;

    strong {
      font-size: 0.92rem;
      color: #1a1a1a;
      font-weight: 800;
    }

    small {
      font-size: 0.74rem;
      color: #64748b;
      line-height: 1.4;
    }
  }

  &__best {
    margin-top: 2px;
    font-size: 0.68rem;
    font-weight: 700;
    color: #c41e1e;
  }
}

/* Preview motifs — each format looks different */
.pv-logo {
  width: 18px;
  height: 14px;
  margin: 0 auto;
  border-radius: 3px;
  background: #c41e1e;
  display: block;
}
.pv-title {
  width: 62%;
  height: 6px;
  margin: 0 auto;
  border-radius: 3px;
  background: #c41e1e;
  display: block;
  opacity: 0.85;
}
.pv-rule {
  width: 100%;
  height: 2px;
  background: #c41e1e;
  opacity: 0.35;
  display: block;
}
.pv-abc {
  display: flex;
  justify-content: center;
  gap: 6px;
  i {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #c41e1e;
    display: block;
    &:nth-child(2) {
      opacity: 0.75;
    }
    &:nth-child(3) {
      opacity: 0.55;
    }
  }
}
.pv-rows {
  display: flex;
  flex-direction: column;
  gap: 3px;
  i,
  em {
    display: block;
    height: 4px;
    border-radius: 2px;
    background: #f0c8c8;
    font-style: normal;
    &:nth-child(1) {
      width: 92%;
    }
    &:nth-child(2) {
      width: 78%;
    }
    &:nth-child(3) {
      width: 86%;
    }
  }
}

.preview-2 {
  gap: 4px;
  .pv-mast {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    border-bottom: 2px solid #c41e1e;
    .pv-logo-sm {
      width: 12px;
      height: 12px;
      border-radius: 3px;
      background: #c41e1e;
      display: block;
    }
    .pv-mast-t {
      flex: 1;
      height: 5px;
      border-radius: 2px;
      background: #e8b4b4;
      display: block;
    }
  }
  .pv-idbar {
    height: 10px;
    border-radius: 4px;
    background: #fdf2f2;
    border: 1px solid #f0dede;
  }
  .pv-card {
    border: 1px solid #f0dede;
    border-radius: 4px;
    padding: 3px 5px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    i {
      height: 3px;
      border-radius: 2px;
      background: #f0c8c8;
      display: block;
      &:first-child {
        width: 40%;
        background: #c41e1e;
        height: 4px;
      }
    }
  }
}

.preview-3 {
  .pv-ribbon {
    width: 70%;
    height: 10px;
    background: #c41e1e;
    clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%);
  }
  .pv-twin {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 8px;
    i {
      height: 8px;
      border-radius: 2px;
      background: #fdf2f2;
      border: 1px solid #f0dede;
      display: block;
    }
  }
}

.preview-4 {
  .pv-band {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 2px solid #c41e1e;
    padding-bottom: 4px;
    i {
      width: 40%;
      height: 6px;
      background: #e8b4b4;
      border-radius: 2px;
      display: block;
    }
    b {
      width: 28px;
      height: 16px;
      border: 1.5px solid #c41e1e;
      border-radius: 3px;
      display: block;
    }
  }
  .pv-grid4 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3px;
    i {
      height: 7px;
      background: #fdf2f2;
      border-bottom: 1px solid #e8b4b4;
      display: block;
    }
  }
  .pv-ledger {
    display: flex;
    flex-direction: column;
    gap: 2px;
    i {
      height: 5px;
      background: repeating-linear-gradient(
        90deg,
        #f0c8c8 0,
        #f0c8c8 70%,
        #c41e1e 70%,
        #c41e1e 100%
      );
      display: block;
      opacity: 0.7;
    }
  }
}

.preview-5 {
  padding: 6px;
  .pv-frame {
    border: 2px double #c41e1e;
    padding: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    height: 100%;
    .pv-logo {
      width: 14px;
      height: 10px;
    }
    .pv-title {
      width: 55%;
      height: 5px;
    }
    .pv-rows {
      width: 100%;
      em {
        display: block;
        height: 4px;
        background: #f0c8c8;
        border-radius: 2px;
        margin-top: 2px;
        &:first-child {
          width: 90%;
        }
        &:last-child {
          width: 75%;
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.tabulation-format-modal {
  .modal-content,
  .format-modal-content {
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid #f0dede;
  }
}

// Simple global fixes
.vs__dropdown-menu {
  z-index: 10000 !important;
}
</style>
